import { PointerEvent, useRef, useState } from "react";
import { useHold } from "./useHold";

// swipeDistance is how far a row is dragged before letting go acts on it
export const swipeDistance = 80;

// decideDistance is how far the pointer moves before the gesture is read as a swipe or
// a scroll
const decideDistance = 8;

// useSwipe reads a swipe to the right on a list row, and a touch and hold on it. A
// swipe only starts when the pointer moves more across than down, so the list still
// scrolls. Anything marked data-no-swipe (a grip, a checkbox) never starts either.
export function useSwipe(props: {
    disabled?: boolean;
    onSwipe: () => void;

    // onPass runs once as the row crosses swipeDistance
    onPass?: () => void;

    // onHold runs on a touch and hold (see useHold)
    onHold?: (point: { x: number; y: number }) => void;
}) {
    const [offset, setOffset] = useState(0);
    const start = useRef<{ x: number; y: number; id: number } | null>(null);
    const swiping = useRef(false);
    const passed = useRef(false);

    // moved keeps the click that ends a swipe from opening the row
    const moved = useRef(false);

    const onHold = props.onHold;
    const hold = useHold({
        onHold: onHold && (point => {
            start.current = null;

            // the lift that ends the hold isn't a tap on the row
            moved.current = true;
            onHold(point);
        }),
    });

    const reset = () => {
        hold.cancel();
        start.current = null;
        swiping.current = false;
        passed.current = false;
        setOffset(0);
    };

    return {
        offset: offset,
        passed: offset >= swipeDistance,

        // wasSwipe is true for the click that ends a swipe
        wasSwipe: () => {
            const value = moved.current;
            moved.current = false;
            return value;
        },

        handlers: {
            onPointerDown: (e: PointerEvent) => {
                if (props.disabled || e.button !== 0) return;
                if ((e.target as HTMLElement).closest("[data-no-swipe]")) return;

                moved.current = false;
                start.current = { x: e.clientX, y: e.clientY, id: e.pointerId };
                hold.start(e);
            },
            onPointerMove: (e: PointerEvent) => {
                const from = start.current;
                if (!from || from.id !== e.pointerId) return;

                const dx = e.clientX - from.x;
                const dy = e.clientY - from.y;

                if (Math.abs(dx) > decideDistance || Math.abs(dy) > decideDistance) hold.cancel();

                if (!swiping.current) {
                    if (Math.abs(dy) > decideDistance && Math.abs(dy) >= Math.abs(dx)) {
                        start.current = null;
                        return;
                    }

                    if (Math.abs(dx) < decideDistance || Math.abs(dx) <= Math.abs(dy)) return;

                    swiping.current = true;
                    moved.current = true;
                    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
                }

                const next = Math.max(0, dx);
                if (next >= swipeDistance && !passed.current) {
                    passed.current = true;
                    if (props.onPass) props.onPass();
                }

                if (next < swipeDistance) passed.current = false;
                setOffset(next);
            },
            onPointerUp: () => {
                const done = swiping.current && passed.current;
                reset();
                if (done) props.onSwipe();
            },
            onPointerCancel: reset,
        },
    };
}
